import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "@/components/ui/sonner";

const API = `${window.location.origin}/api`;

/**
 * Custom hook: loads doctors, insurances on mount;
 * loads treatments whenever the selected insurance changes.
 */
export function useAppData() {
  const [doctors, setDoctors] = useState([]);
  const [assistants, setAssistants] = useState([]);
  const [insurances, setInsurances] = useState([]);
  const [treatments, setTreatments] = useState([]);
  const [categories, setCategories] = useState([]);
  // Mappa {code → cost} dei prezzi "Base" (BDD) per i codici comparabili
  // Indiretto. Popolata dall'endpoint /treatments quando il foglio
  // selezionato non è già "Base". Usata dall'odontogramma per mostrare
  // il doppio prezzo (assicurazione + BDD) nel dropdown prestazioni.
  const [baseCosts, setBaseCosts] = useState({});
  // Stato del listino caricato — espone `fallbackUsed` e il `sheetName`
  // risolto dal backend. Usato dalla pagina Piano di Cura per mostrare
  // un banner ROSSO quando l'assicurazione selezionata non matcha un
  // foglio listino e il backend ha fatto fallback su "Base" → in quel
  // caso i prezzi visualizzati sono BDD (privato), NON quelli
  // dell'assicurazione. Bug guard contro casi tipo "Baldi Maria Teresa"
  // (31/05/2026): InSalute selezionata ma listino caduto su Base.
  const [treatmentsMeta, setTreatmentsMeta] = useState({
    sheetName: "",
    fallbackUsed: false,
    insuranceQueried: "",
  });
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Reusable loader for doctors/assistants/insurances
  const loadBaseData = useCallback(async ({ bustCache = false } = {}) => {
    const params = bustCache ? { _t: Date.now() } : undefined;
    const [dRes, aRes, iRes] = await Promise.all([
      axios.get(`${API}/ghl/doctors`, { params }),
      axios.get(`${API}/ghl/assistants`, { params }),
      axios.get(`${API}/ghl/insurances`, { params }),
    ]);
    setDoctors(dRes.data.doctors || []);
    setAssistants(aRes.data.assistants || []);
    setInsurances(iRes.data.insurances || []);
  }, []);

  // Doctors + assistants + insurances (once)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setIsLoadingData(true);
      try {
        await loadBaseData();
      } catch (err) {
        console.error("Error loading data:", err);
        if (!cancelled) toast.error("Errore nel caricamento dei dati");
      } finally {
        if (!cancelled) setIsLoadingData(false);
      }
    })();
    return () => { cancelled = true; };
  }, [loadBaseData]);

  // Load treatments for a given insurance name.
  // Quando l'utente sta lavorando su un reparto "Indiretti", il backend
  // restituisce anche `baseExtraTreatments` (tutte le prestazioni del
  // listino BDD non presenti nel foglio assicurazione). Le concateniamo
  // alla lista visibile marcandole con `isBaseOnly:true` (il flag arriva
  // già dal backend, lo manteniamo).
  const loadTreatments = useCallback(async (insuranceName, repartoName = "", { bustCache = false } = {}) => {
    try {
      const params = { insurance: insuranceName };
      if (repartoName) params.reparto = repartoName;
      if (bustCache) params._t = Date.now();
      const res = await axios.get(`${API}/treatments`, { params });
      const baseExtra = res.data.baseExtraTreatments || [];
      const merged = [...(res.data.treatments || []), ...baseExtra];
      setTreatments(merged);
      setCategories(res.data.categories || []);
      setBaseCosts(res.data.baseCosts || {});
      // Espone meta del listino così la UI può mostrare un avviso se il
      // backend è caduto in fallback "Base" per un'assicurazione non
      // Base/BDD (= prezzi visualizzati sono BDD anziché assicurazione).
      setTreatmentsMeta({
        sheetName: res.data.sheetName || "",
        fallbackUsed: !!res.data.fallbackUsed,
        insuranceQueried: res.data.insuranceQueried || insuranceName,
      });
    } catch (err) {
      console.error("Error loading treatments:", err);
    }
  }, []);

  // Refresh ALL data (GHL + Google Sheets) on demand
  const refreshAll = useCallback(async (currentInsuranceName = "", currentReparto = "") => {
    setIsRefreshing(true);
    try {
      await Promise.all([
        loadBaseData({ bustCache: true }),
        loadTreatments(currentInsuranceName, currentReparto, { bustCache: true }),
      ]);
      toast.success("Dati aggiornati da GHL e Google Sheets");
    } catch (err) {
      console.error("Refresh error:", err);
      toast.error("Errore durante l'aggiornamento");
    } finally {
      setIsRefreshing(false);
    }
  }, [loadBaseData, loadTreatments]);

  return {
    doctors, assistants, insurances, treatments, categories, baseCosts,
    treatmentsMeta,
    isLoadingData, isRefreshing, loadTreatments, refreshAll,
  };
}

/**
 * Custom hook: debounced patient search.
 */
export function usePatientSearch() {
  const [patients, setPatients] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearching, setIsSearching] = useState(false);

  const searchPatients = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setPatients([]);
      return;
    }
    setIsSearching(true);
    try {
      const res = await axios.get(`${API}/ghl/contacts/search`, { params: { query } });
      setPatients(res.data.contacts || []);
    } catch (err) {
      console.error("Error searching patients:", err);
      toast.error("Errore nella ricerca pazienti");
    } finally {
      setIsSearching(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => searchPatients(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery, searchPatients]);

  return { patients, searchQuery, setSearchQuery, isSearching };
}
